import { Icon } from '@iconify/react';
import { useCallback, useEffect, useState } from 'react';
// material
import {
  Autocomplete,
  Box,
  Button,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import searchFill from '@iconify/icons-eva/search-fill';
import roundFilterList from '@iconify/icons-ic/round-filter-list';
import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import { useDispatch } from 'react-redux';
import { DateRangeStyled, RootStyle, SearchStyle } from '../Global/Styling';
// ---------------------------------------------------------
import 'react-date-range/dist/styles.css'; // main css file
import 'react-date-range/dist/theme/default.css'; // theme css file
import { fDate } from '../../utils/formatTime';
// ----------------------------------------------------------------------

import useLoginSelector from '../../store/Slice/Login/LoginSelector';
import { getCityData, getLoanTypeData, getStatusList } from '../../store/Slice/Global/GlobalSlice';
import useGlobalSelector from '../../store/Slice/Global/GlobalSelector';

export default function RecommendationToolbar({
  filterName,
  setFilterName,
  onFilterName,
  filterDatesRange = [{ startDate: null, endDate: null, key: 'selection' }],
  setFilterDatesRange,
  filterState = {},
  setFilterState
}) {
  const dispatch = useDispatch();
  const [isOpenFilter, openFilter] = useState(false);

  const { LoginData } = useLoginSelector();
  const { CityDrop, LoanTypeDrop, StatusDrop } = useGlobalSelector();

  const handleRemoveFilter = (e) => {
    if (typeof setFilterDatesRange === 'function') {
      setFilterDatesRange([
        {
          startDate: null,
          endDate: null,
          key: 'selection'
        }
      ]);
    }
    if (typeof setFilterState === 'function') {
      setFilterState({
        ...filterState,
        product: ' ',
        productId: null,
        city: null,
        cityId: null,
        status: ' ',
        statusId: null
      });
    }
    if (typeof setFilterName === 'function') {
      setFilterName('');
    }
  };

  const getLoanMasterApi = useCallback(() => {
    const obj = {
      companyId: LoginData?.companyId,
      userId: LoginData?.userId
    };
    dispatch(getLoanTypeData(obj));
  }, [LoginData]);

  //  city api
  const handleGetState = useCallback(
    (optionVal) => {
      const data = {
        ...(optionVal && { stateId: optionVal }),
        companyId: LoginData?.companyId,
        userId: LoginData?.userId,
        searchLiteral: '',
        sortOrder: 'desc'
      };

      dispatch(getCityData(data)); // fetch city data
    },
    [LoginData?.companyId, LoginData?.userId] // dependencies
  );

  useEffect(() => {
    getLoanMasterApi(); // loan master list
    handleGetState(null); // city list
    dispatch(getStatusList(58)); // status list
  }, []);

  const startDate = filterDatesRange?.[0]?.startDate;
  const endDate = filterDatesRange?.[0]?.endDate;

  return (
    <>
      {isOpenFilter && (
        <Box px={3} pt={2} pb={1}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={4}>
              <Select
                fullWidth
                size="small"
                value={filterState.product}
                onChange={(e) => {
                  const prevData = filterState;
                  const inputVal = JSON.parse(e.target.value);
                  prevData.product = e.target.value;
                  prevData.productId = inputVal.loanId;
                  setFilterState({ ...filterState, prevData });
                }}
                inputProps={{
                  className: `textInput customSelectBox dissabledMenu${filterState.product}`
                }}
              >
                <MenuItem value=" " disabled>
                  Select Product
                </MenuItem>
                {LoanTypeDrop?.map((productItem, productIndex) => (
                  <MenuItem key={productItem?.value} value={productItem?.value}>
                    {productItem.label}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid item xs={12} sm={6} md={4} sx={{ position: 'relative' }}>
              <Box className="displayDateRange">
                {startDate != null ? `${fDate(startDate)} to ` : ''}
                {endDate != null ? fDate(endDate) : ''}
                {!startDate && !endDate ? (
                  <Typography className="dissabledMenu">Select Date Range</Typography>
                ) : (
                  ''
                )}
              </Box>
              <Select
                fullWidth
                size="small"
                inputProps={{
                  className: 'textInput'
                }}
                MenuProps={{
                  className: 'menuOpened'
                }}
                value=" "
                className="datesSelect"
                style={{ width: '100%' }}
              >
                <DateRangeStyled
                  editableDateInputs={false}
                  onChange={(item) =>
                    typeof setFilterDatesRange === 'function' &&
                    setFilterDatesRange([item.selection])
                  }
                  moveRangeOnFirstSelection={false}
                  ranges={
                    filterDatesRange?.length > 0
                      ? filterDatesRange
                      : [{ startDate: null, endDate: null, key: 'selection' }]
                  }
                  className="dateRangePicker"
                />
              </Select>
            </Grid>
            <Grid item xs={12} sm={6} md={3.5}>
              <Autocomplete
                disablePortal
                id="combo-box-demo8"
                size="small"
                options={CityDrop || []}
                getOptionLabel={(option) => (option.label ? option.label : option.label)}
                renderOption={(props, option) => <li {...props}>{option.label}</li>}
                renderInput={(params) => (
                  <TextField {...params} fullWidth placeholder="Select City" size="small" />
                )}
                value={filterState !== null ? filterState.city : null}
                onChange={(e, newValue) => {
                  const prevData = filterState;
                  prevData.city = newValue !== null ? newValue : null;
                  prevData.cityId = newValue !== null ? newValue.value : null;
                  setFilterState({ ...filterState, prevData });
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={0.5} sx={{ textAlign: 'center' }}>
              <Tooltip title="Reset Filter">
                <IconButton
                  sx={{
                    border: '1px solid #eaedf1',
                    borderRadius: '4px',
                    backgroundColor: '#fff',
                    '&:hover': { backgroundColor: '#f4f6f8' }
                  }}
                  onClick={(e) => {
                    handleRemoveFilter(e);
                  }}
                >
                  <FilterListOffIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
        </Box>
      )}

      <RootStyle
        sx={{
          borderBottom: '1px solid #e2e8f0',
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: 1.5,
          p: { xs: 2, sm: 2.5 },
          bgcolor: '#ffffff'
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          gap={1.5}
          sx={{ width: { xs: '100%', sm: 'auto' } }}
        >
          <TextField
            size="small"
            value={filterName}
            onChange={onFilterName}
            placeholder="Search Application..."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Icon icon={searchFill} style={{ color: '#94a3b8', fontSize: 18 }} />
                </InputAdornment>
              )
            }}
            sx={{
              width: { xs: '100%', sm: 260 },
              '& .MuiOutlinedInput-root': {
                bgcolor: '#fff',
                borderRadius: '10px',
                fontSize: '13px',
                '& fieldset': { borderColor: '#e2e8f0' },
                '&:hover fieldset': { borderColor: '#cbd5e1' },
                '&.Mui-focused fieldset': { borderColor: '#0284c7' }
              }
            }}
          />

          <Button
            variant="outlined"
            onClick={() => openFilter(!isOpenFilter)}
            startIcon={<Icon icon={roundFilterList} />}
            sx={{
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '13px',
              color: isOpenFilter ? '#0284c7' : '#475569',
              borderColor: isOpenFilter ? '#0284c7' : '#e2e8f0',
              bgcolor: isOpenFilter ? 'rgba(2, 132, 199, 0.06)' : '#fff',
              height: 40,
              px: 2,
              '&:hover': {
                borderColor: '#0284c7',
                bgcolor: 'rgba(2, 132, 199, 0.06)',
                color: '#0284c7'
              }
            }}
          >
            Filters
          </Button>
        </Box>
      </RootStyle>
    </>
  );
}
